import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs'
import { catchError, filter, map, skip, switchMap, take, tap } from 'rxjs/operators'
import { environment } from 'src/environments/environment'

import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { User } from '@classes/User'

import { AuthenticationService } from './authentication.service'
import { EndpointOptions, RequestService } from './request.service'
import { ToastService } from './toast.service'
import { policyDeniedOrganization } from './organization-sign-in.interceptor'
import { isLoginNavigation } from '../utilities/account-access-navigation'

// Result types
export type UserResult = {
    id: string,
    name: string,
    username: string,
    email: string,
    company: string,
    organization_id: string,
    picture: string,
    last_login: string,
    created_on: string,
    employee_number?: string,
    /** Kartorium super admin flag (Kartorium staff only, distinct from client "super user" role) */
    superAdmin?: boolean,
    employeeNumber?: string
}

// GET /user returns the application profile; organization/project lists return DB rows.
// Breadcrumb: backend/endpoints/users.ts:handleGetUser owns the camelCase response.
type CurrentUserResult = Pick<User, 'id' | 'name' | 'username' | 'email' | 'company' | 'organizationID' | 'picture' | 'lastLogin' | 'createdOn' | 'superAdmin' | 'employeeNumber'>

// Conversion functions
export function userFromResult(result: UserResult | CurrentUserResult) {
    return new User(
        result.id,
        result.name,
        result.username,
        result.email,
        result.company,
        'createdOn' in result ? result.organizationID : result.organization_id,
        result.picture,
        'createdOn' in result ? result.lastLogin : result.last_login,
        'createdOn' in result ? result.createdOn : result.created_on,
        result.superAdmin,
        'employee_number' in result ? result.employee_number ?? result.employeeNumber : result.employeeNumber
    )
}

const organizationUsersSubject = new BehaviorSubject<User[]>([])

@Injectable({
    providedIn: 'root'
})
export class UserService {
    private _userUrl = environment.api + '/user'

    private _currentUser: BehaviorSubject<User> = new BehaviorSubject<User>(undefined)
    public currentUser$: Observable<User> = this._currentUser.pipe(skip(1))
    get currentUser(): User { return this._currentUser.getValue() }
    set currentUser(user: User) { this._currentUser.next(user) }

    public organizationUsers$: Observable<User[]> = organizationUsersSubject.pipe(skip(1))
    get organizationUsers(): User[] { return organizationUsersSubject.getValue() }
    set organizationUsers(users: User[]) { organizationUsersSubject.next(users) }

    constructor(
        private _authService: AuthenticationService,
        private _http: HttpClient,
        private _requestService: RequestService,
        private _toastService: ToastService,
        private _router: Router,
    ) {
        this.getCurrentUser().pipe(
            switchMap(user => {
                // Breadcrumb: PUT /user validates this in backend/endpoints/users.ts
                // before writing users.last_login. Locale strings have no timezone.
                user.lastLogin = new Date().toISOString()
                return this.updateUser(user)
            }),
            switchMap(() =>
                this.getOrganizationsUsers().pipe(tap(users => this.organizationUsers = users))
            )
        ).subscribe()
    }

    getCurrentUser() {
        if (this.currentUser) {
            return of(this.currentUser)
        } else {
            return this._authService.user$.pipe(
                filter(user => user !== undefined && user !== null),
                take(1),
                switchMap(() => this.getUser()),
                catchError((error: unknown) => {
                    // Account selection precedes application access for an orgless identity.
                    if (error instanceof HttpErrorResponse && error.status === 403 &&
                        ['ACCOUNT_ACCESS_MISSING', 'ACCOUNT_ACCESS_UNAVAILABLE', 'ACCOUNT_ACCESS_INACTIVE'].includes(error.error?.code) &&
                        isLoginNavigation(this._router)) return EMPTY
                    return this.handleError<User>("Failed to get current user.")(error)
                }),
                tap(user => this.currentUser = user)
            )
        }
    }

    getUser(): Observable<User> {
        const subject = this._authService.currentUser?.sub
        const organization = this._authService.currentUser?.org_id
        const url = `${environment.api}/user`
        const options = { error: { operation: 'Get Current Database User', retry: true } } as EndpointOptions

        return this._requestService.get<CurrentUserResult>(url, options).pipe(
            map(userResult => userFromResult(userResult)),
            tap(user => this._authService.setCanonicalMemberID(user.id, subject, organization)),
        )
    }

    getOrganizationsUsers(): Observable<User[]> {
        const url = `${environment.api}/users/organization`

        return this._http.get<UserResult[]>(url).pipe(
            catchError(this.handleError<UserResult[]>("Failed to get users.")),
            map(us => us.map(u => userFromResult(u)))
        )
    }

    updateUser(user: User): Observable<User> {
        const url = `${environment.api}/user`
        const endpointOptions = { error: { operation: 'Update User' } } as EndpointOptions

        return this._requestService.update<User>(url, user, endpointOptions).pipe(
            tap(user => this.currentUser = user)
        )
    }

    updateMemberDetails(memberID: string, details: { name?: string, employeeNumber?: string | null }): Observable<{ name: string, employeeNumber: string | null }> {
        const url = `${environment.api}/org/member/${memberID}`
        const endpointOptions = { error: { operation: 'Update Member Details', toast: true } } as EndpointOptions

        return this._requestService.update<{ name: string, employeeNumber: string | null }>(url, details, endpointOptions)
    }

    /**
     * Handle Http operation that failed.
     * Let the app continue.
     * @param operation - name of the operation that failed
     * @param result - optional value to return as the observable result
     */
    private handleError<T>(operation = 'Operation did not complete.', result?: T) {
        return (error: unknown): Observable<T> => {
            // The policy interceptor owns recovery navigation. Stop bootstrap instead
            // of emitting an undefined user and continuing into PUT /user.
            if (policyDeniedOrganization(error)) return EMPTY

            const message = error instanceof HttpErrorResponse && error.status === 409 &&
                error.error?.code === 'local_user_identity_conflict' && typeof error.error.error === 'string'
                ? error.error.error : operation
            this._toastService.toast({ title: "Error", message, color: "red" })

            // TODO: send the user error to remote logging infrastructure
            console.error(error) // log to console instead

            // TODO: better job of transforming error for user consumption
            // this.log(`${operation} failed: ${error.message}`);

            // A failed profile must not reach bootstrap's lastLogin/PUT /user work.
            return result === undefined ? EMPTY : of(result)
        }
    }
}
